/**
 * A check-in template is a named set of tracks — the primary logging path.
 *
 * It exists for adherence, not features. Logging that takes six taps and a
 * scroll stops happening in week two, and the resulting gaps are
 * indistinguishable from real signal. Two taps and a drag is the target.
 *
 * Templates are entirely user-defined. There is deliberately no conditional
 * logic, no branching, and no required-versus-optional rules: every rule added
 * is a decision the user has to make at 1pm with food going cold.
 */

import type { Clock } from '../ports/clock.js'
import type { Entry, Measurement, NewEntry } from './entry.js'
import { err, invalid, ok, type Result, type ValidationError } from './result.js'
import type { Timestamp } from './timestamp.js'
import type { Track, TrackValue } from './track.js'

export interface CheckInTemplate {
  readonly id: string
  readonly name: string
  readonly description?: string
  /** Tracks in the order they will appear in the form. */
  readonly trackIds: readonly string[]
  /** Applied to every entry this template creates. */
  readonly defaultTagIds: readonly string[]
  readonly position: number
  readonly archived: boolean
  readonly createdAt: Timestamp
  readonly updatedAt: Timestamp
}

const MAX_NAME_LENGTH = 60
const MAX_DESCRIPTION_LENGTH = 300

export interface ValidateTemplateOptions {
  readonly existingTemplates?: readonly CheckInTemplate[]
  readonly tracks?: readonly Track[]
}

export function validateTemplate(
  template: CheckInTemplate,
  options: ValidateTemplateOptions = {},
): Result<CheckInTemplate> {
  const errors: ValidationError[] = []
  const name = template.name.trim()

  if (name === '') {
    errors.push(invalid('template.name.empty', 'A check-in needs a name.', 'name'))
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.push(
      invalid(
        'template.name.tooLong',
        `A name can be at most ${String(MAX_NAME_LENGTH)} characters.`,
        'name',
      ),
    )
  }

  const clash = (options.existingTemplates ?? []).some(
    (other) =>
      other.id !== template.id &&
      !other.archived &&
      other.name.trim().toLowerCase() === name.toLowerCase(),
  )
  if (name !== '' && clash) {
    errors.push(
      invalid(
        'template.name.duplicate',
        `You already have a check-in called "${name}".`,
        'name',
      ),
    )
  }

  if (
    template.description !== undefined &&
    template.description.length > MAX_DESCRIPTION_LENGTH
  ) {
    errors.push(
      invalid(
        'template.description.tooLong',
        `A description can be at most ${String(MAX_DESCRIPTION_LENGTH)} characters.`,
        'description',
      ),
    )
  }

  if (template.trackIds.length === 0) {
    errors.push(
      invalid(
        'template.tracks.empty',
        'A check-in needs at least one track.',
        'trackIds',
      ),
    )
  }

  const seen = new Set<string>()
  for (const trackId of template.trackIds) {
    if (seen.has(trackId)) {
      errors.push(
        invalid(
          'template.tracks.duplicate',
          'The same track is listed twice.',
          'trackIds',
        ),
      )
    }
    seen.add(trackId)
  }

  if (options.tracks !== undefined) {
    const known = new Set(options.tracks.map((track) => track.id))
    for (const trackId of template.trackIds) {
      if (!known.has(trackId)) {
        errors.push(
          invalid(
            'template.tracks.unknown',
            'This check-in refers to a track that no longer exists.',
            'trackIds',
          ),
        )
      }
    }
  }

  return errors.length > 0 ? err<CheckInTemplate>(...errors) : ok(template)
}

/** One field in a form the user is about to fill in. */
export interface DraftSlot {
  readonly track: Track
  /** Undefined means unset. Saving leaves it out rather than recording a zero. */
  readonly value: TrackValue | undefined
  /** True when the track has been archived since the template was written. */
  readonly archived: boolean
}

export interface EntryDraft {
  readonly templateId: string
  readonly at: Timestamp
  readonly tagIds: readonly string[]
  readonly slots: readonly DraftSlot[]
  /**
   * Tracks the template lists that no longer exist at all.
   *
   * Reported rather than fatal: a template referring to a deleted track should
   * still open, so the user can log the rest and fix it when convenient.
   */
  readonly missingTrackIds: readonly string[]
}

export interface ResolveOptions {
  /** Supply to backdate; omit to stamp the current moment. */
  readonly at?: Timestamp
}

/**
 * Opens a template as a form, stamped now unless a moment is supplied.
 *
 * An archived track is flagged, not dropped: the user chose it, and silently
 * removing a field is more surprising than showing it with a note. A track that
 * no longer exists at all cannot be rendered, so it is skipped and listed.
 */
export function resolveTemplate(
  template: CheckInTemplate,
  tracks: readonly Track[],
  clock: Clock,
  options: ResolveOptions = {},
): EntryDraft {
  const byId = new Map(tracks.map((track) => [track.id, track]))
  const slots: DraftSlot[] = []
  const missingTrackIds: string[] = []

  // Template order, not track order: the user arranged these deliberately.
  for (const trackId of template.trackIds) {
    const track = byId.get(trackId)
    if (track === undefined) {
      missingTrackIds.push(trackId)
      continue
    }
    slots.push({ track, value: undefined, archived: track.archived })
  }

  return {
    templateId: template.id,
    at: options.at ?? clock.now(),
    tagIds: [...template.defaultTagIds],
    slots,
    missingTrackIds,
  }
}

/** Sets one slot's value, leaving the rest of the draft alone. */
export function setSlotValue(
  draft: EntryDraft,
  trackId: string,
  value: TrackValue | undefined,
): EntryDraft {
  return {
    ...draft,
    slots: draft.slots.map((slot) =>
      slot.track.id === trackId ? { ...slot, value } : slot,
    ),
  }
}

/**
 * Turns a filled-in draft into the input for a new entry.
 *
 * Unset slots produce **no measurement**. That is the whole reason `value` is
 * optional rather than defaulted: a track the user skipped and a track they
 * recorded as zero are different claims, and only one of them belongs in an
 * average.
 */
export function draftToEntry(draft: EntryDraft, id: string): NewEntry {
  const measurements: Measurement[] = draft.slots
    .filter((slot): slot is DraftSlot & { value: TrackValue } => slot.value !== undefined)
    .map((slot) => ({ trackId: slot.track.id, value: slot.value }))

  return {
    id,
    at: draft.at,
    tagIds: draft.tagIds,
    templateId: draft.templateId,
    measurements,
  }
}

/** Whether anything has been filled in yet. */
export function draftHasValues(draft: EntryDraft): boolean {
  return draft.slots.some((slot) => slot.value !== undefined)
}

/** Entries produced by a template, for comparing like with like in a report. */
export function entriesFromTemplate(
  entries: readonly Entry[],
  templateId: string,
): Entry[] {
  return entries.filter((entry) => entry.templateId === templateId)
}
